/** @jsxImportSource theme-ui */
import { useState } from 'react';
import { Box, Flex, Input, Label, Select, Text } from 'theme-ui';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import {
    useLazyPeekVossDeadLettersQuery,
    useReplayVossDeadLettersMutation,
} from '@/store/apiService';
import Button from '@/components/Button';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import DataTable from '@/components/DataTable';

const queues = [
    'voss-provision-company',
    'voss-create-customer',
    'voss-update-customer',
    'voss-add-license',
    'voss-update-subscription',
    'voss-create-usage',
    'voss-add-nonapp-license',
    'voss-add-nonlicense-product',
    'voss-subscription-changed',
];

const columns = [
    { columnKey: 'MessageId', accessor: 'MessageId', Header: 'Message Id', width: '240px' },
    {
        columnKey: 'EnqueuedTimeUtc',
        accessor: 'EnqueuedTimeUtc',
        Header: 'Enqueued (UTC)',
        width: '140px',
        Cell: ({ cell }: any) =>
            cell.value ? format(new Date(cell.value), 'd MMM yyyy, HH:mm:ss') : '',
    },
    { columnKey: 'DeliveryCount', accessor: 'DeliveryCount', Header: 'Deliveries', width: '80px' },
    {
        columnKey: 'DeadLetterReason',
        accessor: 'DeadLetterReason',
        Header: 'Reason',
        width: '180px',
    },
    {
        columnKey: 'DeadLetterErrorDescription',
        accessor: 'DeadLetterErrorDescription',
        Header: 'Description',
        width: '30%',
    },
    {
        columnKey: 'Body',
        accessor: 'Body',
        Header: 'Body',
        Cell: ({ cell }: any) => (
            <Box
                as="pre"
                sx={{ fontSize: 0, whiteSpace: 'pre-wrap', textAlign: 'left', margin: 0 }}
            >
                {cell.value}
            </Box>
        ),
    },
];

const DeadLetters = () => {
    const [queue, setQueue] = useState(queues[0]);
    const [max, setMax] = useState(50);
    const [reason, setReason] = useState('');
    const [confirm, setConfirm] = useState(false);
    const [peek, peekState] = useLazyPeekVossDeadLettersQuery();
    const [replay, replayState] = useReplayVossDeadLettersMutation();
    const messages: any[] = peekState.data?.Messages ?? [];

    const doPeek = () =>
        peek({ Queue: queue, Max: max })
            .unwrap()
            .catch((err) =>
                toast.error(err?.data?.ResponseStatus?.Message ?? err?.message ?? 'Peek failed', {
                    autoClose: 5000,
                })
            );

    const doReplay = () =>
        replay({ Queue: queue, Max: max, DeadLetterReason: reason.trim() || undefined })
            .unwrap()
            .then((res: any) => {
                setConfirm(false);
                toast.success(`Replayed ${res.ReplayedCount}, left in DLQ ${res.SkippedCount}`);
                doPeek();
            })
            .catch((err) => {
                setConfirm(false);
                toast.error(err?.data?.ResponseStatus?.Message ?? err?.message ?? 'Replay failed', {
                    autoClose: 5000,
                });
            });

    return (
        <Flex sx={{ flexDirection: 'column', gap: 3 }}>
            <Text>
                Service Bus stamps every dead-lettered message with the reason it was dead-lettered;
                this is the only place that reason exists when the consumer never reached its own
                error log. Replay moves the messages back onto the queue and removes them from the
                DLQ.
            </Text>
            <Flex sx={{ gap: 3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: '280px' }}>
                    <Label htmlFor="dlqQueue">Queue</Label>
                    <Select id="dlqQueue" value={queue} onChange={(e) => setQueue(e.target.value)}>
                        {queues.map((q) => (
                            <option key={q} value={q}>
                                {q}
                            </option>
                        ))}
                    </Select>
                </Box>
                <Box sx={{ width: '120px' }}>
                    <Label htmlFor="dlqMax">Max</Label>
                    <Input
                        id="dlqMax"
                        type="number"
                        min={1}
                        max={500}
                        value={max}
                        onChange={(e) => setMax(Number(e.target.value) || 1)}
                    />
                </Box>
                <Box sx={{ minWidth: '240px' }}>
                    <Label htmlFor="dlqReason">Only replay reason (optional)</Label>
                    <Input
                        id="dlqReason"
                        placeholder="e.g. ProcessingException"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </Box>
                <Button variant="secondary" isLoading={peekState.isFetching} onClick={doPeek}>
                    Peek
                </Button>
                <Button
                    variant="danger"
                    isLoading={replayState.isLoading}
                    onClick={() => setConfirm(true)}
                >
                    Replay
                </Button>
            </Flex>
            {confirm ? (
                <ConfirmationModal
                    hideOnClickOutside
                    loading={replayState.isLoading}
                    headingText="Replay dead letters"
                    content={`Move up to ${max} dead-lettered message(s) from ${queue}${
                        reason.trim() ? ` with reason "${reason.trim()}"` : ''
                    } back onto the queue? If the root cause is not fixed they will dead-letter again.`}
                    onNo={() => setConfirm(false)}
                    onYes={doReplay}
                />
            ) : null}
            {peekState.data ? (
                <Text>
                    {peekState.data.Count} message(s) in the dead-letter queue of{' '}
                    <b>{peekState.data.Queue}</b>
                </Text>
            ) : null}
            <Box sx={{ table: { whiteSpace: 'normal' } }}>
                <DataTable
                    data={messages}
                    columns={columns}
                    isLoading={peekState.isLoading}
                    isFetching={peekState.isFetching}
                />
            </Box>
        </Flex>
    );
};

export default DeadLetters;
